import type { ChangeSetFileContent, ChangeSetInfo, FileChangeSummary } from "@racler/protocol";

/** Status letter shown in the review list: M modified, A added, D deleted, R renamed. */
export function changeLetter(op: FileChangeSummary["op"]): "M" | "A" | "D" | "R" {
  return op === "create" ? "A" : op === "delete" ? "D" : op === "rename" ? "R" : "M";
}

/** "M src/a.ts", "R old.ts → new.ts" (plain text, e.g. for chat summaries and tests). */
export function changeLabel(f: Pick<FileChangeSummary, "op" | "path" | "oldPath">): string {
  return f.op === "rename" && f.oldPath ? `R ${f.oldPath} → ${f.path}` : `${changeLetter(f.op)} ${f.path}`;
}

/** What the two sides of the Monaco diff editor show (create: empty → new, delete: old → empty). */
export function diffSides(f: ChangeSetFileContent): {
  original: string;
  modified: string;
  originalPath: string;
} {
  return { original: f.original ?? "", modified: f.modified ?? "", originalPath: f.oldPath ?? f.path };
}

/**
 * Open editor tabs that have UNSAVED human edits in files this ChangeSet touches (targets and rename sources).
 * Applying is blocked while any exist: human edits take precedence, and racler.dev never auto-saves them.
 */
export function dirtyConflicts(
  files: readonly Pick<FileChangeSummary, "path" | "oldPath">[],
  dirtyPaths: readonly string[],
): string[] {
  const touched = new Set(files.flatMap((f) => (f.oldPath ? [f.path, f.oldPath] : [f.path])));
  return dirtyPaths.filter((p) => touched.has(p));
}

export const isFinal = (s: ChangeSetInfo["status"]): boolean =>
  s === "applied" || s === "conflicted" || s === "failed" || s === "rejected" || s === "discarded";

/** One-line, user-facing status of a ChangeSet for the conversation. */
export function statusText(cs: Pick<ChangeSetInfo, "status" | "files" | "message">): string {
  const n = cs.files.length;
  const files = `${n} ${n === 1 ? "file" : "files"}`;
  switch (cs.status) {
    case "draft":
      return "Preparing a proposal…";
    case "pending_approval":
      return `Proposed changes to ${files} — pending your approval`;
    case "approved":
      return "Approved";
    case "applying":
      return "Applying…";
    case "applied":
      return `${files} changed`;
    case "rejected":
      return "Changes rejected";
    case "conflicted":
      return "Conflict detected — Project was not modified";
    case "failed":
      return cs.message ?? "Applying the changes failed";
    case "discarded":
      return "Proposal discarded (the response was stopped before it was complete)";
  }
}

/** Open tabs whose file the apply touched must be reloaded from disk (they would otherwise show stale text). */
export function pathsToReload(cs: Pick<ChangeSetInfo, "status" | "files">): string[] {
  if (cs.status !== "applied") return [];
  return cs.files.flatMap((f) => (f.oldPath ? [f.path, f.oldPath] : [f.path]));
}
